import {ViewStore} from '../base/view-store';
import {action, makeObservable, observable} from 'mobx';
import {ApiRepository} from '../base/api-repository';
import {LabelStore} from '../base/label-store';
import {UserStore} from '../user/user-store';
import {periodIdFromRange} from '../base/dateUtils';
import {DateRange} from 'react-day-picker';

export class FeedbackStore extends ViewStore {
    @observable
    payload?: FeedbackStatisticsPayload = undefined;
    @observable
    variables: FeedbackVariables = new FeedbackVariables();

    constructor(
        private api: ApiRepository,
        labelStore: LabelStore,
        private userStore: UserStore,
    ) {
        super(labelStore);
        makeObservable(this);
    }

    @action
    onEnter(): void {
        this.load(async () => {
            await this.refreshPayload();
            const {from, to} = this.payload!.feedbackTooltip.period;

            this.variables.periodId = periodIdFromRange({from: new Date(from), to: new Date(to)} as DateRange);
        });
    }

    @action
    onCompanyChange(companyId: string): void {
        this.variables.companyId = companyId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onSegmentChange(segment: string): void {
        this.variables.segment = segment;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onPeriodChange(periodId: string): void {
        this.variables.periodId = periodId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    setCustomDateSelected(selected: boolean): void {
        this.variables.isCustomDate = selected;
    }

    async refreshPayload(): Promise <void> {
        const queryParameters: Record<string, string> = {};

        if (this.variables.companyId !== undefined) {
            queryParameters['company-id'] = this.variables.companyId;
        }
        if (this.variables.periodId !== undefined) {
            queryParameters['period-id'] = this.variables.periodId;
        }
        if (this.variables.segment !== undefined) {
            queryParameters.segment = this.variables.segment;
        }

        const queryParamsString = new URLSearchParams(queryParameters).toString();

        this.payload = await this.api.get<FeedbackStatisticsPayload>(`/feedback/employee/${this.userStore.payload!.employeeId}?${queryParamsString}`);
    }
}

interface FeedbackStatisticsPayload {
    feedbackFilter: FeedbackFilterPayload;
    companiesCard: FeedbackDashboardData[];
    moticheckFeedbackCard: MoticheckQuestionFeedback[],
    segmentsCard: FeedbackDashboardData[],
    questionCards: FeedbackQuestionCard[],
    feedbackTooltip: FeedbackTooltipPayload
}

interface FeedbackFilterPayload {
    periods: FeedbackFilter[],
    companies: FeedbackFilter[],
    segments: FeedbackFilter[],
}

interface FeedbackFilter {
    id: string;
    label?: string;
    selected: boolean;
}

export interface FeedbackDashboardData {
    id?: string,
    value: string,
    barpoint: FeedbackBarpoint,
    scoreChange: number,
    focused: boolean,
}

export interface FeedbackQuestionCard {
    questionText: string,
    questionId: string,
    segment: string,
    barpoint: FeedbackBarpoint,
    feedbackAnswers: AnswerWithCommentOnFeedbackQuestionCard[]
}

export interface MoticheckQuestionFeedback {
    segment: string,
    questionText: string,
    feedbackQuestionId: string,
    answers: AnswerWithCommentOnFeedbackQuestionCard[]
}

export interface AnswerWithCommentOnFeedbackQuestionCard {
    text: string,
    datetime: string,
    answer: string,
    questionId: string
}

export interface FeedbackBarpoint {
    value: FeedbackDatapoint,
    compareTo: FeedbackDatapoint
}

export interface FeedbackDatapoint {
    prepared: number;
    asked: number;
    answered: number;
    scorePercentage: number;
    participationPercentage: number;
}

interface FeedbackTooltipPayload {
    period: Period;
    companyName?: string;
    compareToName?: string;
}

export interface Period {
    from: string;
    to: string;
}

class FeedbackVariables {
    @observable
    companyId?: string = undefined;
    @observable
    segment?: string = undefined;
    @observable
    periodId?: string = undefined;
    @observable
    isCustomDate: boolean = false;

    constructor() {
        makeObservable(this);
    }
}
